import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PrimaryButton, SecondaryButton, Field, inputCls } from "@/components/ui-kit";
import { toast } from "sonner";
import { Mail, Plus, X, Pencil, Trash2, Eye, Tag } from "lucide-react";

/** Category catalog: keep IDs stable; colors adapt to the app palette. */
export const TEMPLATE_CATEGORIES = [
  { id: "ventas", label: "Ventas", cls: "bg-[#00C853]/10 text-[#008C3A] border-[#00C853]/30" },
  { id: "cobranza", label: "Cobranza", cls: "bg-[#D50000]/10 text-[#B71C1C] border-[#D50000]/30" },
  { id: "servicio", label: "Servicio", cls: "bg-[#2962FF]/10 text-[#1E40AF] border-[#2962FF]/30" },
  { id: "otro", label: "Otro", cls: "bg-neutral-100 text-neutral-700 border-neutral-300" },
];

function CategoryChip({ id, size = "sm" }) {
  const c = TEMPLATE_CATEGORIES.find((x) => x.id === id) || TEMPLATE_CATEGORIES[3];
  const cls = size === "sm"
    ? "text-[9px] px-1.5 py-0.5"
    : "text-[10px] px-2 py-0.5";
  return (
    <span className={`font-mono-kpi uppercase tracking-[0.1em] border inline-flex items-center gap-0.5 ${cls} ${c.cls}`}>
      <Tag className="w-2.5 h-2.5" />{c.label}
    </span>
  );
}

function TemplateModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [data, setData] = useState({
    name: initial?.name || "",
    subject: initial?.subject || "",
    body: initial?.body || "",
    category: initial?.category || "otro",
  });
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Debounced live preview
  useEffect(() => {
    if (!data.subject && !data.body) { setPreviewHtml(""); return; }
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data: p } = await api.post("/email-templates/preview", { subject: data.subject, body: data.body });
        setPreviewHtml(p.body_html || "");
      } catch (e) {
        // silent — will retry on next edit
      } finally { setPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [data.subject, data.body]);

  const save = async () => {
    if (!data.name.trim() || !data.subject.trim()) return toast.error("Nombre y asunto son obligatorios");
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/email-templates/${initial.id}`, data);
      else await api.post("/email-templates", data);
      toast.success(isEdit ? "Plantilla actualizada" : "Plantilla creada");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-5xl border-t-4 border-[#FF5A00] max-h-[95vh] flex flex-col" data-testid="email-template-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Plantilla de correo</div>
            <div className="font-display font-bold text-xl">{isEdit ? "Editar plantilla" : "Nueva plantilla"}</div>
          </div>
          <button onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 divide-x divide-neutral-100 overflow-hidden">
          {/* Form */}
          <div className="p-6 overflow-y-auto space-y-4">
            <Field label="Nombre de la plantilla *" hint="Sólo tú lo verás para elegirla al enviar.">
              <input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className={inputCls} placeholder="Ej: Bienvenida contrato nuevo" data-testid="tpl-name" />
            </Field>
            <Field label="Categoría">
              <div className="flex flex-wrap gap-1.5" data-testid="tpl-category-picker">
                {TEMPLATE_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setData({ ...data, category: c.id })}
                    data-testid={`tpl-category-${c.id}`}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 font-mono-kpi text-[10px] uppercase tracking-[0.1em] border ${data.category === c.id ? c.cls + " ring-1 ring-current" : "bg-white text-neutral-500 border-neutral-300 hover:border-neutral-500"}`}
                  >
                    <Tag className="w-3 h-3" />{c.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Asunto *">
              <input value={data.subject} onChange={(e) => setData({ ...data, subject: e.target.value })} className={inputCls} placeholder="Asunto del correo" data-testid="tpl-subject" />
            </Field>
            <Field label="Cuerpo" hint="Párrafos separados por doble salto de línea. El saludo ('Estimado(a) ...') y la firma se agregan automáticamente.">
              <textarea rows={14} value={data.body} onChange={(e) => setData({ ...data, body: e.target.value })} className={`${inputCls} font-mono text-xs`} placeholder="Escribe aquí el cuerpo del correo…" data-testid="tpl-body" />
            </Field>
          </div>

          {/* Preview */}
          <div className="p-6 bg-neutral-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1.5">
                <Eye className="w-3 h-3" />Vista previa
              </div>
              {previewLoading && <span className="text-[10px] font-mono-kpi uppercase text-neutral-400">Actualizando…</span>}
            </div>
            {previewHtml ? (
              <iframe title="preview" srcDoc={previewHtml} className="w-full h-[calc(100vh-320px)] min-h-[400px] bg-white border border-neutral-200" data-testid="tpl-preview-frame" />
            ) : (
              <div className="border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500 h-64 flex items-center justify-center">
                Escribe un asunto y cuerpo para ver la vista previa.
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-neutral-200 bg-white">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving} data-testid="tpl-save">
            {saving ? "Guardando…" : (isEdit ? "Guardar cambios" : "Crear plantilla")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default function EmailTemplatesSection({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {new:true} | {...tpl}
  const [catFilter, setCatFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-templates");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (catFilter === "all") return items;
    return items.filter((t) => (t.category || "otro") === catFilter);
  }, [items, catFilter]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    TEMPLATE_CATEGORIES.forEach((cat) => {
      c[cat.id] = items.filter((t) => (t.category || "otro") === cat.id).length;
    });
    return c;
  }, [items]);

  const remove = async (tpl) => {
    if (!window.confirm(`¿Eliminar plantilla "${tpl.name}"?`)) return;
    try {
      await api.delete(`/email-templates/${tpl.id}`);
      toast.success("Plantilla eliminada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <section className="mb-8 bg-white border border-neutral-200 p-6" data-testid="settings-email-templates-section">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Plantillas de correo</div>
          <div className="font-display font-bold text-xl mt-1 flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#FF5A00]" />Plantillas personalizadas
          </div>
          <div className="text-sm text-neutral-500 mt-1 max-w-xl">
            Crea plantillas reutilizables con vista previa en vivo. Están disponibles al redactar cualquier correo desde el CRM.
          </div>
        </div>
        {isAdmin && (
          <PrimaryButton onClick={() => setEditing({ new: true })} data-testid="tpl-new-btn">
            <Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />Nueva plantilla
          </PrimaryButton>
        )}
      </div>

      {loading ? (
        <div className="font-mono-kpi text-xs text-neutral-400 py-6 text-center uppercase tracking-widest">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500">
          Aún no hay plantillas guardadas. {isAdmin && <>Haz clic en <b>Nueva plantilla</b> para crear la primera.</>}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="tpl-cat-filter">
            <button
              onClick={() => setCatFilter("all")}
              className={`px-2.5 py-1 font-mono-kpi text-[10px] uppercase tracking-[0.1em] border ${catFilter === "all" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-neutral-300 hover:border-neutral-500"}`}
              data-testid="tpl-cat-filter-all"
            >Todas · {counts.all}</button>
            {TEMPLATE_CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCatFilter(c.id)}
                data-testid={`tpl-cat-filter-${c.id}`}
                className={`inline-flex items-center gap-1 px-2.5 py-1 font-mono-kpi text-[10px] uppercase tracking-[0.1em] border ${catFilter === c.id ? c.cls + " ring-1 ring-current" : "bg-white text-neutral-500 border-neutral-300 hover:border-neutral-500"}`}
              >
                <Tag className="w-3 h-3" />{c.label} · {counts[c.id] || 0}
              </button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div className="border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500">
              Sin plantillas en esta categoría.
            </div>
          ) : (
            <div className="divide-y divide-neutral-100 border border-neutral-200">
              {filtered.map((t) => (
                <div key={t.id} data-testid={`tpl-row-${t.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <CategoryChip id={t.category || "otro"} />
                      <div className="font-bold text-sm truncate">{t.name}</div>
                    </div>
                    <div className="text-xs text-neutral-500 truncate mt-0.5">{t.subject}</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setEditing(t)} data-testid={`tpl-edit-${t.id}`} className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-600 hover:text-[#FF5A00] hover:bg-neutral-50 px-2 py-1 border border-neutral-200">
                      <Pencil className="w-3 h-3 inline mr-1 -mt-0.5" />Ver / Editar
                    </button>
                    {isAdmin && (
                      <button onClick={() => remove(t)} data-testid={`tpl-delete-${t.id}`} className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#D50000] hover:bg-[#D50000]/5 px-2 py-1 border border-[#D50000]/30">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <TemplateModal
          initial={editing.new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}
